import React, { useEffect, useState } from 'react';
import { isMobile } from 'lib/utils/UiUtils';
import { Chart, registerables, BarController } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import CustomBarController from './CustomBarController';
import { formatCurrency } from 'lib/utils/currency';
import { orZero } from 'lib/utils/NumUtils';

// https://www.chartjs.org/docs/latest/charts/bar.html
const BarGraph = (props) => {
    const percentageGoal = orZero(parseFloat((props.projected - props.target) / props.projected) * 100).toFixed(2);
    const [isGraphInitialized, setIsGraphInitialized] = useState(false);
    const [graphReady, setGraphReady] = useState(false);
    const chartEl = React.createRef();
    const detailsContainer = React.createRef();
    const mobile = isMobile();

    let chart = undefined;
    let data = props.data;
    let dailyGoal = props.dailyTarget || (props.target / props.monthLength);
    let maxValue = chart ? chart.scales.y.max : props.target;
    let maxHeight = chart ? chart.scales.y.maxHeight : 0;

    const { dataset } = props;

    if (dataset) {
        data.datasets[0].data = [...dataset];
    }

    const updateData = (d, _d, i, ctx) => {
        let perc = _d / dailyGoal;
        let color = '';
        let aboveColor = props.swapped ? 'rgba(98, 63, 234, 1)' : 'rgba(102, 255, 204, 1)';
        let belowColor = props.swapped ? 'rgba(102, 255, 204, 1)' : 'rgba(98, 63, 234, 1)';

        function makeGradient(dVal) {
            let graphHeight = maxHeight;// canvas.height;
            let yStart = graphHeight - (graphHeight * (dVal / maxValue)) + 10;
            let gradient = ctx.createLinearGradient(0, yStart, 0, graphHeight);
            gradient.addColorStop(0, 'rgba(102, 255, 204, 1)');
            gradient.addColorStop(1, 'rgba(98, 63, 234, 1)');
            return gradient;
        }

        if (_d >= dailyGoal) {
            color = aboveColor;
            // if within 10% show gradient
            if (perc <= 1.1 && props.swapped) {
                color = makeGradient(_d);
            }
        } else {
            color = belowColor;
            // if within 10% show gradient
            if (perc >= .9 && !props.swapped) {
                color = makeGradient(_d);
            }
        }

        d.backgroundColor[i] = color;
        d.hoverBackgroundColor[i] = color;
    }

    // create chart
    useEffect(() => {
        Chart.register(...registerables);
        Chart.register(annotationPlugin);
        Chart.register(CustomBarController);

        Chart.defaults.elements.bar.borderRadius = mobile ? 5 : 10;
        Chart.defaults.elements.bar.pointRadius = mobile ? 5 : 10;
        Chart.defaults.elements.bar.barThickness = mobile ? 5 : 10;
        Chart.defaults.scale.ticks.padding = mobile ? 10 : 20;
        Chart.defaults.color = 'rgba(255,255,255,1)';
        Chart.defaults.font.family = 'Grotesk';
        Chart.defaults.font.size = mobile ? 12 : 16;

        if (data && chartEl.current && !isGraphInitialized && props.dataReady && !chart) {
            let canvas = chartEl.current;
            let ctx = canvas.getContext('2d');

            if (!chart) {
                console.log('chart data', data)
                chart = new Chart(ctx, { type: 'CustomBar', data: data, options: props.options });
                setIsGraphInitialized(true);
            }
        }
    }, [data]);

    // modify data and update chart, after it's been created
    useEffect(() => {
        if (chart && chartEl.current) {
            //let canvas = chartEl.current;
            let ctx = chartEl.current.getContext('2d');
            maxValue = chart.scales.y.max;
            maxHeight = chart.scales.y.maxHeight;

            // update data gradients now that we have correct canvas height:
            chart.data.datasets.forEach((d) => {
                d.backgroundColor = [];
                d.hoverBackgroundColor = [];
                d.data.forEach((_d, i) => {
                    updateData(d, _d, i, ctx);
                });
            });

            chart.update();

            // manually line up the bottom number columns with graph:
            let container = detailsContainer.current;

            if (props.mobile && container && chart.scales.y) {
                let scale = chart.scales.y;
                container.style.paddingLeft = scale.width + 'px';
                setGraphReady(true);
            }

            if (!props.mobile) {
                setGraphReady(true);
            }
        }
    }, [data]);

    // separate effect for destroy to only be called once
    useEffect(() => {
        return () => {
            if (chart) {
                chart.destroy();
                chart = null;
                setIsGraphInitialized(false);
            }
        }
    }, []);

    // replace datasets with colors:
    // green = above, purple = below, gradient = within 10% of daily goal
    let aboveClass = props.swapped ? ' below' : ' above';
    let belowClass = props.swapped ? ' above' : ' below';

    return (
        <div className={'graph-container bar-graph' + (props.mobile ? ' mobile' : '') + (graphReady ? ' ready' : '')}>

            {props.header}

            <div className={'graph ' + (props.taller ? 'taller' : '')} key={props.name}>
                {/* <Bar data={data} options={props.options} ref={chart} /> */}
                <canvas id="chartjs-graph" key={props.name + "-canvas"} width="100%" height="100%" ref={chartEl} />
            </div>

            <div className="details-container" ref={detailsContainer}>
                {/* {
                    props.average ? (
                        <div className="detail">
                            <div className="inner">
                                <div className="label">Average Daily {props.title}</div>
                                <h1>{props.price === true ? formatCurrency(props.average) : parseFloat(props.average).toFixed(props.price === 'even' ? 0 : 2)}</h1>
                            </div>
                        </div>
                    ) : null
                } */}

                {/* {
                    props.average && props.dailyGoal ? (
                        <div className={"percentage-container" + (props.centered ? ' centered' : '')}> { /*threshold={props.threshold} swapped={isSwapped}>
                            {`${percentageDaily}%`}
                        </div>
                    ) : null
                */}

                {
                    (typeof props.target != 'undefined') ? (
                        <div className="detail target">
                            <div className="inner">
                                <div className="label">Target {mobile ? '' : props.name}</div>
                                <h1 className="target-price">{props.price === true ? formatCurrency(props.target) : parseFloat(props.target).toFixed(props.price === 'even' ? 0 : 2)}</h1>
                            </div>
                        </div>
                    ) : null
                }

                {
                    (typeof props.actual != 'undefined') ? (
                        <div className="detail actual">
                            <div className="inner">
                                <div className="label">Actual {mobile ? '' : (props.name + ' ')}(MTD)</div>
                                <h1>{props.price === true ? formatCurrency(props.actual) : orZero(parseFloat(props.actual).toFixed(2))}</h1>
                            </div>
                        </div>
                    ) : null
                }

                {
                    (typeof props.projected != 'undefined') ? (
                        <div className="detail projected" projected="true">
                            <div className="inner">
                                <div className="label">Predicted {mobile ? '' : props.name}</div>
                                <div className="row">
                                    <h1 className={percentageGoal >= 100 ? 'pace-above' : ''}>{props.price === true ? formatCurrency(props.projected) : orZero(parseFloat(props.projected).toFixed(props.price === 'even' ? 0 : 2))}</h1>
                                    {
                                        (typeof props.projected != 'undefined') && (typeof props.targets != 'undefined') ? (
                                            <div className={'percentage-container' + (percentageGoal > 0 ? aboveClass : belowClass)}>
                                                { /* <div className="percentage-container" percentageValue={percentageGoal} threshold={props.threshold} swapped={isSwapped}> */}
                                                {`${percentageGoal}%`}
                                            </div>
                                        ) : null
                                    }
                                </div>
                            </div>
                        </div>
                    ) : null
                }

                {
                    (typeof props.dailyGoal != 'undefined') ? (
                        <div className="detail" daily="true">
                            <h1>{props.price === true ? formatCurrency(props.dailyGoal) : parseFloat(props.dailyGoal).toFixed(props.price === 'even' ? 0 : 2)}</h1>
                            <div className="label">Daily {props.name}{props.comparison ? '' : ' Goal'}{props.comparison ? `, ${props.comparison}` : ''}</div>
                        </div>
                    ) : null
                }
            </div>

        </div>
    )
}

export default BarGraph